import React, { useEffect, useMemo, useState } from 'react'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { Col, Container, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { Button } from '../../../components/Button/Button'
import { MultiSelect, Option } from 'react-multi-select-component'
import { addToast } from '../../../store/toast/toastSlice'
import { Modal } from '../../../components/Modal/Modal'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'

interface PIIRedactionCategoryOption extends Option {
  description: string
}

const PII_REDACTION_CATEGORIES: PIIRedactionCategoryOption[] = [
  {
    value: 'financial',
    label: 'Financial Information',
    description: 'Financial and account-related information, including banking details',
  },
  { value: 'personal', label: 'Personal Information', description: 'Names, contact details, and digital identifiers.' },
  {
    value: 'medical',
    label: 'Medical Information',
    description: 'Health-related information, including medical conditions, treatments, and healthcare identifiers.',
  },
  { value: 'location', label: 'Location Information', description: 'Geographical and nationality-related details, including addresses and country of origin.' },
  {
    value: 'demographic',
    label: 'Demographic Information',
    description: 'Characteristics related to identity, such as age, gender, occupation, political affiliation, and religion.',
  },
  {
    value: 'confidential',
    label: 'Confidential  Information',
    description: 'Sensitive and confidential information, such as credit card numbers, social security numbers, and passwords.',
  },
  {
    value: 'miscellaneous',
    label: 'Miscellaneous  Information',
    description: 'Other identifiable data, including dates, events, languages, organizations, and URLs.',
  },
]

export const PIIRedactionConfig: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: account } = useGetAccountQuery({ accountId })

  const redactedPiiCategories = useMemo(() => {
    if (!account) {
      return []
    }
    return account.data.effectiveConfig.piiRedactionCategories.map(
      (category) => PII_REDACTION_CATEGORIES.find((option) => option.value === category)?.label || category,
    )
  }, [account])

  const [showEditPiiRedactionCategoriesModal, setShowEditPiiRedactionCategoriesModal] = useState<boolean>(false)
  const [piiRedactionEnforced, setPiiRedactionEnforced] = useState(false)

  useEffect(() => {
    if (!account) return
    setPiiRedactionEnforced(account.data.enterpriseConfig.piiRedactionCategories != null)
  }, [account])

  return (
    <>
      {showEditPiiRedactionCategoriesModal && (
        <EditPIIRedactionCategoriesModal show={showEditPiiRedactionCategoriesModal} onHide={() => setShowEditPiiRedactionCategoriesModal(false)} />
      )}
      <div className='lead-medium my-3'>PII Redaction Configuration</div>
      <Container className='data-retention-defaults-container zocks-py-1.25 px-4'>
        {piiRedactionEnforced ? (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>PII Redaction is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='gy-3'>
          <Col className='p-0'>
            <Container>
              <Row className='zocks-mb-1.25'>
                <Col>
                  <h4>PII Redaction</h4>
                </Col>
              </Row>
              <Row className='mb-3'>
                <Col>
                  <div className='paragraph-small'>
                    Personally Identifiable Information (PII) includes sensitive details like names, contact info, and financial data. Enable redaction to
                    automatically remove these details from transcripts. Redacted data won't be captured in summaries, forms and other places.
                  </div>
                  <div className='paragraph-small mt-2'>
                    Select or deselect categories below to control which types of PII are redacted in your transcriptions. Your preferences will apply to future
                    transcriptions.
                  </div>
                </Col>
              </Row>
              <Row className='mb-3'>
                <Col>
                  <ScopedBlock
                    quota={{
                      quota: 'retention.piiRedactionEnabled.value',
                    }}
                    missingScopeAction={MissingScopeAction.UPGRADE}
                  >
                    <Button variant='primary' onClick={() => setShowEditPiiRedactionCategoriesModal(true)} disabled={piiRedactionEnforced}>
                      <i className='bi bi-plus' />
                      Edit PII redaction categories
                    </Button>
                  </ScopedBlock>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
      <div className='lead-medium my-3'>Redacted PII categories</div>
      {account ? (
        <div>{redactedPiiCategories.length ? `Currently redacted PII categories: ${redactedPiiCategories.join(', ')}` : 'No redacted PII categories'}</div>
      ) : null}
    </>
  )
}

export interface EditPIIRedactionCategoriesModalProps {
  show: boolean
  onHide: () => void
}

const EditPIIRedactionCategoriesModal: React.FC<EditPIIRedactionCategoriesModalProps> = ({ show, onHide }) => {
  const dispatch = useAppDispatch()

  const { hasScope } = useZocksAuth()

  const hasAccountConfigUpdateScope = hasScope('**', 'account_config:update', ['account_config:admin'])

  const accountId = useAppSelector(selectAccountId)

  const { data: account } = useGetAccountQuery({ accountId })
  const [selectedPiiRedactionCategories, setSelectedPiiRedactionCategories] = useState<PIIRedactionCategoryOption[]>([])
  useEffect(() => {
    if (!account) {
      return
    }
    setSelectedPiiRedactionCategories(
      PII_REDACTION_CATEGORIES.filter((category) => account.data.effectiveConfig.piiRedactionCategories.includes(category.value)),
    )
  }, [account])

  const [updateAccount, { isLoading: isAccountUpdating }] = useUpdateAccountMutation()

  const handleSave = async () => {
    if (!account) {
      return
    }
    const accountConfig = { ...account.data.config, piiRedactionCategories: selectedPiiRedactionCategories.map((category) => category.value) }
    try {
      await updateAccount({
        accountId,
        payload: {
          config: accountConfig,
        },
      }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Success', messages: ['PII Redaction settings saved successfully!'] }))
      onHide()
    } catch (e) {
      console.error(e)
    }
  }

  const ArrowRenderer = ({ expanded }: { expanded: boolean }) => (
    <>{expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}</>
  )

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>PII Redaction Categories</h4>
        </Col>
      </Row>
      <Row>
        <MultiSelect
          hasSelectAll
          disableSearch
          disabled={!hasAccountConfigUpdateScope}
          options={PII_REDACTION_CATEGORIES}
          isLoading={isAccountUpdating}
          value={selectedPiiRedactionCategories}
          onChange={setSelectedPiiRedactionCategories}
          labelledBy={'Select'}
          valueRenderer={(selected: Option[]) => {
            return selected.length ? `${selected.length} ${selected.length > 1 ? 'categories' : 'category'} selected` : 'No categories selected'
          }}
          ItemRenderer={PIIRedactionCategoryItem}
          ArrowRenderer={ArrowRenderer}
          ClearSelectedIcon={<i className='bi bi-x-lg mx-3' />}
          overrideStrings={{ selectAll: 'Select/Deselect All' }}
        />
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSave}
      footerPrimaryActionLoading={isAccountUpdating}
      footerPrimaryActionDisabled={!hasAccountConfigUpdateScope}
      centered={true}
    />
  )
}

interface PIIRedactionCategoryItemProps {
  checked: boolean
  option: PIIRedactionCategoryOption
  disabled?: boolean
  onClick: () => void
}

const PIIRedactionCategoryItem = ({ checked, option, onClick }: PIIRedactionCategoryItemProps) => {
  const tooltip = <Tooltip id={`pii_tooltip_${option.value}`}>{option.description}</Tooltip>

  return (
    <div className='item-renderer'>
      <Form.Check
        key={option.value}
        type='checkbox'
        id={`option_${option.value}`}
        label={
          <div className='d-flex gap-3 align-items-center'>
            <span className='paragraph-medium'>{option.label}</span>
            {option.value ? (
              <OverlayTrigger overlay={tooltip}>
                <i className='bi bi-info-circle' />
              </OverlayTrigger>
            ) : null}
          </div>
        }
        checked={checked}
        onChange={onClick}
        value={option.value}
      />
    </div>
  )
}
